import { stringify } from 'qs';

const logger = (message: any) => {
	if ('development' === process.env.NODE_ENV) {
		console.log(message);
	}
};

const Api = {
	map: process.env.REACT_APP_API_MAP || null,
	getApiBase: () => {
		let base =
			process.env.REACT_APP_API_BASE ||
			process.env.STORYBOOK_API_BASE ||
			null;
		// @ts-ignore
		if (window.TBK_GUTENBERG && window.TBK_GUTENBERG.apiBase) {
			// @ts-ignore
			base = window.TBK_GUTENBERG.apiBase;
		}
		return (base || '/api/').replace(/\/$/, ''); // strip trailing slash
	},
	logger: logger,
	obfuscate: false, // ( 'development' !== process.env.NODE_ENV ),
	prepare: function () {
		const me: any = this;
		if (!me.map) {
			me.map = new Promise((resolve, reject) => {
				me.request('/api/map')
					.then((map: any) => {
						me.map = {};

						me.applyMap(map);

						resolve(map);
					})
					.catch((e: any) => {
						reject('Unable to obtain API map');
					});
			});
		}
		return Promise.resolve(me.map);
	},
	applyMap: function (map: any) {
		if (!this.map) {
			// @ts-ignore
			this.map = {};
		}
		map.forEach((point: any) => {
			// @ts-ignore
			this.map[point.function] = point;
			// @ts-ignore
			this[point.function] = (...data) => {
				const req = this.determineRequest(point, data);

				if (req) {
					return this.request(
						req.endpoint,
						req.method,
						req.parameters,
					);
				}
			};
		});
	},
	getUri: function (func: any, ...args: any) {
		// @ts-ignore
		if (this.map[func]) {
			// @ts-ignore
			const req = this.determineRequest(this.map[func], args);
			return req ? this.getApiBase() + req.endpoint : undefined;
		}
		return undefined;
	},
	determineRequest: (point: any, parms: any) => {
		const req = {
			method: point.method.toUpperCase(),
			endpoint: point.route,
			parameters: parms || [],
		};

		// One by one, replace any {parameters} in the endpoint with the next item in parameters
		// Note, this does not yet know how to handle optional parameters parameters.
		// See https://www.slimframework.com/docs/v3/objects/router.html#how-to-create-routes
		try {
			req.endpoint = req.endpoint.replace(/{[^}]+}/g, function () {
				if (req.parameters.length) {
					return req.parameters.shift();
				}
				throw new Error(
					'Not enough parameters passed for ' + req.endpoint,
				);
			});
		} catch (e) {
			logger(e);
			return false;
		}

		// If there are any parameters left, then we un-array-ify $parameters ( just set it to the first member )
		// Note, there is a limitation here that we can never pass more than one argument ( apart from arguments
		// that go into the endpoint ) to an SDK method.
		req.parameters = req.parameters.length ? req.parameters.shift() : null;

		return req;
	},
	activeRequests: {} as any,
	request: function (endpoint: any, method: any, data: any) {
		if (!method) {
			method = 'GET';
		} else {
			method = method.toUpperCase();
		}
		let url = this.getApiBase() + endpoint;
		const abortKey =
			method +
			'/' +
			url +
			(data && data._abort_key ? '/' + data._abort_key : '');
		let args = {
			credentials: 'include',
			cache: 'no-cache',
			method: method,
			headers: {
				Accept: 'application/json,text/plain',
			},
		} as any;

		const dataStringified = data ? stringify(data) : null;
		const now = new Date();
		const timestamp = parseFloat(
			`${now.getSeconds() + now.getMilliseconds() / 1000}`,
		);
		if (this.activeRequests[abortKey]) {
			if (this.activeRequests[abortKey].data === dataStringified) {
				// this request is already active with the exact same data.  Don't bother calling the API again.
				return new Promise((resolve, reject) => {});
			}
			if (this.activeRequests[abortKey].controller) {
				this.activeRequests[abortKey].controller.abort();
			}
		}
		this.activeRequests[abortKey] = {
			controller: window.AbortController
				? new window.AbortController()
				: null,
			data: dataStringified,
			timestamp,
		};
		if (this.activeRequests[abortKey].controller) {
			args.signal = this.activeRequests[abortKey].controller.signal;
		}

		if (data) {
			if (['GET', 'DELETE'].indexOf(method) !== -1) {
				// delete & get parms are passed in via query string
				url +=
					'?' +
					(this.obfuscate ? 'r=' + this.e(data) : dataStringified);
			} else {
				args.body =
					data instanceof FormData ? data : this.toFormData(data);
				if (this.obfuscate) {
					let object = {} as any;
					// @ts-ignore
					args.body.forEach((value: any, key: any) => {
						object[key] = value;
					});
					args.body = this.toFormData({
						r: this.e(object),
					});
				}
			}
		}

		if (['GET', 'PUT', 'POST', 'DELETE'].indexOf(method) === -1) {
			args.method = 'POST';
			args.headers['X-HTTP-Method-Override'] = method;
		}

		return new Promise((resolve, reject) => {
			fetch(url, args)
				.then((response) => {
					if (this.activeRequests[abortKey].timestamp !== timestamp) {
						// in case this browser doesn't have AbortController, or if that doesn't work ( looking at you Safari )
						// then we check the timestamp if the response isn't from the current request, then we abort, doing nothing.
						return;
					}

					delete this.activeRequests[abortKey]; // successful, delete this so same request can run again at another time
					const type = response.headers.get('content-type')
						? response.headers.get('content-type')?.split(';')[0]
						: 'text/html';

					if (503 === response.status) {
						reject();
					}

					const handleJsonResponse = (json: any) => {
						if (json.hasOwnProperty('data')) {
							if (json.success) {
								resolve(json.data);
							} else {
								reject(json.data);
							}
						} else if (json.message) {
							reject(json.message);
						}
					};

					try {
						if ('text/plain' === type) {
							response.text().then((text) => {
								handleJsonResponse(this.d(text));
							});
						} else if ('application/json' === type) {
							response.json().then((json) => {
								handleJsonResponse(json);
							});
						} else {
							reject(response);
						}
					} catch (e) {
						logger(e);
						reject();
					}
				})
				.catch((e) => {
					if ('The user aborted a request.' !== e.message) {
						// Above exception is when we've cancelled the request ( because the user is typing and another
						// request has come in before the previous one has finished.  All other exceptions, throw again.
						throw e;
					}
				});
		});
	},
	// takes a {} object and returns a FormData object
	// thanks https://gist.github.com/ghinda/8442a57f22099bdb2e34
	toFormData: (object: any) => {
		const toFormData = (obj: any, form?: any, namespace?: any) => {
			let fd = form || new FormData();
			let formKey;

			for (let property in obj) {
				if (obj.hasOwnProperty(property)) {
					if (namespace) {
						formKey = namespace + '[' + property + ']';
					} else {
						formKey = property;
					}

					// if the property is an object, but not a File, use recursivity.
					if (obj[property] instanceof Date) {
						fd.append(formKey, obj[property].toISOString());
					} else if (
						typeof obj[property] === 'object' &&
						!(obj[property] instanceof File)
					) {
						toFormData(obj[property], fd, formKey);
					} else {
						// if it's a string or a File object
						fd.append(formKey, obj[property]);
					}
				}
			}
			return fd;
		};

		return object ? toFormData(object) : null;
	},
	/**
	 * Encode Object - calling it "e" for obfuscation
	 *
	 * @param object
	 */
	e: function (object: any) {
		const shift = Math.floor(Math.random() * 100000) + 100000;
		return (
			this.cs(btoa(shift + ''), 5) +
			'/' +
			this.cs(btoa(encodeURI(JSON.stringify(object))), shift)
				.split('')
				.reverse()
				.join('')
		);
	},
	/**
	 * Decode string - calling it "d" for obfuscation
	 *
	 * @param str
	 */
	d: function (str: any) {
		let shift: any = 0;
		const matches = str.match(/([^/]+)\/(.*)$/);
		if (matches) {
			shift = atob(this.cs(matches[1], 21));
			str = matches[2];
		}
		return JSON.parse(
			atob(this.cs(str.split('').reverse().join(''), 26 - (shift % 26))),
		);
	},
	/**
	 * CaesarShift - calling it cs to obfuscate
	 */
	cs: function (str: any, amount: any): any {
		// Wrap the amount
		if (amount < 0) return this.cs(str, amount + 26);

		// Make an output variable
		let output = '';

		amount = amount % 26;

		// Go through each character
		for (let i = 0; i < str.length; i++) {
			// Get the character we'll be appending
			let c = str[i];

			// If it's a letter...
			if (c.match(/[a-z]/i)) {
				// Get its code
				const code = str.charCodeAt(i);

				// Uppercase letters
				if (code >= 65 && code <= 90)
					c = String.fromCharCode(((code - 65 + amount) % 26) + 65);
				// Lowercase letters
				else if (code >= 97 && code <= 122)
					c = String.fromCharCode(((code - 97 + amount) % 26) + 97);
			}

			// Append
			output += c;
		}

		// All done!
		return output;
	},
};

export default Api;
